import { PAGE_SIZE_OPTIONS, Pagination } from "@/components/ui/pagination";
import { SearchInput } from "@/components/ui/search-input";
import { FilterBar } from "@/components/work-items/filter-bar";
import { NewWorkItemButtons } from "@/components/work-items/new-buttons";
import type { WorkItemsListResponse } from "@/components/work-items/project-list";
import { QuickFilters } from "@/components/work-items/quick-filters";
import { WORK_ITEM_SORT_KEYS, WorkItemsTable } from "@/components/work-items/work-items-table";
import { api } from "@/lib/api";
import { usePersistedFilters } from "@/lib/persisted-filters";
import { paginationSearchFields, sortSearchFields } from "@/lib/search";
import { createFileRoute, useLoaderData } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";

/**
 * `/work-items` — global work-item listing. Port of the Next
 * `WorkItemsList` (`showGlobalHeader`). The full filterable list:
 * quick-filter pills + the filter bar + search, the table with
 * per-row workflow actions, the floating bulk-edit toolbar and
 * pagination.
 */

const TYPE_IDS = ["feature", "bug", "standalone", "hotfix"] as const;
const PRIORITY_VALUES = ["low", "normal", "high", "urgent"] as const;

const workItemsSearchSchema = z.object({
  q: z.string().optional(),
  status: z.string().optional(),
  priority: z.enum(["low", "normal", "high", "urgent"]).optional(),
  type: z.enum(["feature", "bug", "standalone", "hotfix"]).optional(),
  assignee: z.string().optional(),
  author: z.string().optional(),
  watcher: z.string().optional(),
  project: z.string().optional(),
  release: z.string().optional(),
  environment: z.enum(["dev", "test", "main", "none"]).optional(),
  attachments: z.enum(["any"]).optional(),
  scope: z.enum(["open", "all"]).optional(),
  dueAfter: z.string().optional(),
  dueBefore: z.string().optional(),
  ...sortSearchFields(WORK_ITEM_SORT_KEYS),
  ...paginationSearchFields,
});

type WorkItemsSearch = z.infer<typeof workItemsSearchSchema>;

export const Route = createFileRoute("/_app/work-items/")({
  validateSearch: workItemsSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
    status: search.status,
    priority: search.priority,
    type: search.type,
    assignee: search.assignee,
    author: search.author,
    watcher: search.watcher,
    project: search.project,
    release: search.release,
    environment: search.environment,
    attachments: search.attachments,
    scope: search.scope,
    dueAfter: search.dueAfter,
    dueBefore: search.dueBefore,
    sort: search.sort,
    dir: search.dir,
    page: search.page,
    pageSize: search.pageSize,
  }),
  loader: ({ deps }) => {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(deps)) {
      if (v !== undefined && v !== "") params.set(k, String(v));
    }
    const qs = params.toString();
    return api.get<WorkItemsListResponse>(
      qs ? `/api/work-items/list?${qs}` : "/api/work-items/list",
    );
  },
  component: WorkItemsPage,
});

const WORK_ITEM_FILTER_FIELDS = [
  "q",
  "status",
  "priority",
  "type",
  "assignee",
  "author",
  "watcher",
  "project",
  "release",
  "environment",
  "attachments",
  "scope",
  "dueAfter",
  "dueBefore",
] as const;

function WorkItemsPage() {
  const { t } = useTranslation();
  const { workItems, total, page, pageSize, statuses, users, projects, releases } =
    Route.useLoaderData();
  const search: WorkItemsSearch = Route.useSearch();
  const layout = useLoaderData({ from: "/_app" });
  const currentUserId = layout.user.id;
  // Remember filters per route — `/work-items` and each
  // `/projects/<slug>/work-items` carry their own snapshot in
  // `localStorage`. See `lib/persisted-filters.ts`.
  usePersistedFilters(WORK_ITEM_FILTER_FIELDS);
  // Members (developers etc.) are scoped to "their" items by default;
  // admins / accountants see the full roster.
  // Same rule as the per-project listing: devs / etc. land on
  // "Mine"; admin / accountant on full set; customer (guest-only)
  // on "open" because they have no assignments.
  const defaultMine =
    !layout.isCustomer &&
    layout.user.globalRole !== "admin" &&
    layout.user.globalRole !== "accountant";

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl font-bold">{t("wiIndex.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("wiIndex.total", { count: total })}
          </p>
        </div>
        <NewWorkItemButtons />
      </div>

      <div className="flex flex-col gap-3">
        <QuickFilters currentUserId={currentUserId} defaultMine={defaultMine} />
        <SearchInput placeholder={t("wiIndex.searchPlaceholder")} />
        <FilterBar
          statuses={statuses.map((s) => ({ id: s.id, name: s.name }))}
          types={TYPE_IDS.map((id) => ({ id, name: t(`wiIndex.types.${id}`) }))}
          priorities={PRIORITY_VALUES.map((value) => ({
            value,
            label: t(`wiIndex.priorities.${value}`),
          }))}
          users={users.map((u) => ({ id: u.id, name: u.name ?? t("wiIndex.unnamed") }))}
          projects={projects.map((p) => ({ id: p.id, name: p.name }))}
          releases={releases}
        />
      </div>

      <WorkItemsTable
        workItems={workItems}
        users={users
          .filter((u) => !u.former)
          .map((u) => ({ id: u.id, name: u.name ?? t("wiIndex.unnamed") }))}
        releases={releases}
        urlSort={{ sort: search.sort, dir: search.dir }}
        currentUserId={currentUserId}
        isCustomer={layout.isCustomer}
        canSeeAll={layout.user.globalRole === "admin" || layout.user.globalRole === "accountant"}
        canOverrideStatus={layout.user.globalRole === "admin" || layout.isProjectManager}
      />

      <Pagination
        total={total}
        page={page}
        pageSize={pageSize}
        pageSizes={PAGE_SIZE_OPTIONS}
        storageKey="work-items"
        basePath="/work-items"
        searchParams={{
          q: search.q,
          status: search.status,
          priority: search.priority,
          type: search.type,
          assignee: search.assignee,
          author: search.author,
          watcher: search.watcher,
          project: search.project,
          release: search.release,
          environment: search.environment,
          attachments: search.attachments,
          scope: search.scope,
          dueAfter: search.dueAfter,
          dueBefore: search.dueBefore,
          sort: search.sort,
          dir: search.dir,
          pageSize: search.pageSize,
        }}
      />
    </div>
  );
}
